import { IAccount } from "types/Account/Account";

import { IProviderProcedure } from "../ProviderProcedure";

export enum SpecialtyList {
  FACILITY = "FACILITY",
  ANESTHESIOLOGIST = "ANESTHESIOLOGIST",
  PATHOLOGIST = "PATHOLOGIST",
  PRIMARY_PROVIDER = "PRIMARY_PROVIDER",
  ASSISTANT_PROVIDER = "ASSISTANT_PROVIDER",
}

export const SpecialtyListDict = {
  [SpecialtyList.FACILITY]: "Facility",
  [SpecialtyList.ANESTHESIOLOGIST]: "Anesthesiologist",
  [SpecialtyList.PATHOLOGIST]: "Pathologist",
  [SpecialtyList.PRIMARY_PROVIDER]: "Provider",
  [SpecialtyList.ASSISTANT_PROVIDER]: "Assistant Provider",
};
export const SpecialtyListDictCheckout = {
  [SpecialtyList.FACILITY]: "Facility",
  [SpecialtyList.ANESTHESIOLOGIST]: "Anesthesiologist",
  [SpecialtyList.PATHOLOGIST]: "Pathologist",
  [SpecialtyList.PRIMARY_PROVIDER]: "Physician",
  [SpecialtyList.ASSISTANT_PROVIDER]: "Assistant Provider",
};

/**
 * CLE-2892 — one provider as the Contracts tab's provider pickers read it
 * (`GET /providers/account/:id/options`): no tax id, contact or payout data.
 */
export interface IProviderOption {
  id: string;
  name: string;
  specialty: SpecialtyList;
}

export interface IProvider {
  id: string;
  name: string;
  specialty: SpecialtyList;
  classification: string;
  stripeAccountNumber: string | null;
  finixMerchantId: string | null;
  streetAddress: string;
  city: string;
  state: string;
  zipcode: string | null;
  country: string | null;
  rcm: string;
  npi: string;
  taxPayorIdNumber: string;
  phoneNumber: string;
  email: string;
  mainFacility: boolean;
  account: IAccount;
  reconciliationConfigId: string | null;
  providerProcedures: IProviderProcedure[];
  emailToReceiveNotification: string[];
  emailToReceiveAbnNotification?: string[];
  frontDeskEmployeesToReceiveDailyEmail: string[];
  emailToReceiveReports: string[];
  expirationDays: number;
  paymentContact: {
    contactEmail: string;
    contactName: string;
    contactPhone: string;
    athenaIntegrationEnabled?: boolean;
    athenaPracticeId?: string;
    athenaDepartmentId?: string;
  };
  unitHospitalNumber: string | null;
  createdAt: string;
  updatedAt: string;
  publicContactName: string;
  publicContactRole: string;
  publicContactPhone: string;
  eligibilityEnabled: boolean;
  numberOfProceduresLive?: number;
  quantity: number;
  customPdfContent?: {
    documentTitle: string | null;
    introductionText: string | null;
    summaryLayout: string;
    feeTableLayout: string;
    convenienceServiceFees: string | null;
    showDisclaimer: boolean;
    disclaimerType: string | null;
    showTermsAndConditions: boolean;
    termsAndConditionsText: string | null;
  } | null;
}

export interface IEligibilityProvider {
  firstName?: string;
  middleName?: string;
  lastName: string;
  npi: string;
}

export interface IEligibilityMember {
  firstName: string;
  lastName: string;
  dob: string;
  memberID: string;
}

/**
 * CLE-2845 — why an eligibility check recommends switching the order's payer: which Payer
 * Auto-Selection stage fired (Eligibility › Payer Auto-Selection).
 *
 * MEDICAID_DUAL: a DUAL / MEDICAID plan name, or a PolicyType naming Medicaid, at a facility with a
 * Medicaid rule.
 * MEDICARE_ADVANTAGE: a Commercial payer whose plan looks like Medicare Advantage.
 * PLAN_NAME: the plan name the payer reported names a different payer this account has.
 * OUT_OF_AREA_DEFAULT: an out-of-area Blue Cross member with no recognisable local product, priced
 * as the facility's out-of-area default rule says.
 */
export type RecommendedPayerReason =
  | "MEDICAID_DUAL"
  | "MEDICARE_ADVANTAGE"
  | "PLAN_NAME"
  | "OUT_OF_AREA_DEFAULT";

export interface IRecommendedPayerBasedOnEligibility {
  id: number;
  name: string;
  reason: RecommendedPayerReason;
  /**
   * What decided it: the keyword, the Medicare term, `PolicyType <value>` for a Medicaid match on
   * PolicyType, or the out-of-area signal.
   */
  matchedText?: string;
}
